import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Printer, Mail, CheckCircle2, Pencil, UserMinus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import FreightSummary from "@/components/FreightSummary";
import RateConfDialog from "@/components/RateConfDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { LOGO_URL, COMPANY, currencySymbol } from "@/lib/constants";

const fmtDT = (v) => {
  if (!v) return "";
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const Addr = ({ label, a, date, extraLabel, extraValue }) => (
  <div>
    <div className="mono-label">{label}</div>
    <div className="mt-1 text-sm">
      {a?.name && <div className="font-semibold">{a.name}</div>}
      <div>{a?.street}</div>
      <div>{a?.city}, {a?.state} {a?.zip}</div>
      {a?.contact_name && <div className="text-xs text-zinc-600 mt-1">Contact: {a.contact_name}{a.contact_phone && ` · ${a.contact_phone}`}</div>}
    </div>
    <div className="mt-2 text-xs mono-label">
      {date && <>Date: <span className="font-mono normal-case tracking-normal text-zinc-950">{fmtDT(date)}</span></>}
    </div>
    {extraLabel && (
      <div className="mt-1 text-xs mono-label">{extraLabel}: <span className="font-mono normal-case tracking-normal text-zinc-950">{fmtDT(extraValue) || "—"}</span></div>
    )}
  </div>
);

export default function RateConfirmationView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rc, setRc] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [sendForm, setSendForm] = useState({ to_email: "", cc: "", subject: "", message: "" });

  const removeAndReassign = async () => {
    const ok = window.confirm(
      `Delete Rate Confirmation ${rc.rc_number} and detach carrier "${rc.carrier_name}" from job ${rc.job_number}?\n\n` +
      `You can then assign a new carrier and create a fresh Rate Confirmation.\n\n` +
      `This cannot be undone.`
    );
    if (!ok) return;
    setRemoving(true);
    try {
      await api.delete(`/rate-confirmations/${rc.id}`);
      toast.success(`RC ${rc.rc_number} removed. Carrier detached — assign a new one on the job.`);
      navigate(`/admin/jobs/${rc.job_id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to remove rate confirmation");
    } finally { setRemoving(false); }
  };

  const load = async () => {
    const { data } = await api.get(`/rate-confirmations/${id}`);
    setRc(data);
    setSendForm((f) => ({ ...f, to_email: data.carrier_contact_email || "" }));
  };
  useEffect(() => { load(); }, [id]);

  const send = async (e) => {
    e.preventDefault();
    if (!sendForm.to_email) { toast.error("Enter a carrier email"); return; }
    setSending(true);
    try {
      const payload = {
        to_email: sendForm.to_email,
        subject: sendForm.subject || undefined,
        message: sendForm.message || undefined,
        cc: sendForm.cc ? sendForm.cc.split(",").map((s) => s.trim()).filter(Boolean) : undefined,
      };
      const { data } = await api.post(`/rate-confirmations/${id}/send-email`, payload);
      toast.success(`Rate confirmation sent to ${data.sent_to}`);
      setSendOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  if (!rc) return <div className="p-10 mono-label">Loading rate confirmation...</div>;
  const sym = currencySymbol(rc.currency);

  return (
    <div className="min-h-screen bg-zinc-100">
      <div className="no-print bg-white border-b border-zinc-200 px-6 py-3 flex items-center justify-between">
        <Link to={`/admin/jobs/${rc.job_id}`} className="mono-label">&larr; BACK TO JOB</Link>
        <div className="flex items-center gap-2">
          {rc.email_sent_at && (
            <span className="mono-label !text-emerald-800 inline-flex items-center gap-1" data-testid="rc-sent-badge">
              <CheckCircle2 className="h-3.5 w-3.5" /> Sent to {rc.email_sent_to}
            </span>
          )}
          <Button variant="outline" onClick={() => setEditOpen(true)} className="sharp border-zinc-300" data-testid="rc-edit-btn">
            <Pencil className="h-4 w-4 mr-2" /> Edit
          </Button>
          <Button
            variant="outline"
            onClick={removeAndReassign}
            disabled={removing}
            className="sharp border-rose-700 text-rose-700 hover:bg-rose-50"
            data-testid="rc-remove-reassign-btn"
            title="Delete this RC and detach carrier from the job so a new carrier can be assigned"
          >
            <UserMinus className="h-4 w-4 mr-2" />
            {removing ? "Removing..." : "Remove & Reassign Carrier"}
          </Button>
          <Button variant="outline" onClick={() => setSendOpen(true)} className="sharp border-zinc-950" data-testid="rc-send-btn">
            <Mail className="h-4 w-4 mr-2" /> {rc.email_sent_at ? "Resend to carrier" : "Send to carrier"}
          </Button>
          <Button onClick={() => window.print()} className="sharp bg-zinc-950 hover:bg-zinc-800" data-testid="print-rc">
            <Printer className="h-4 w-4 mr-2" /> Print / Save PDF
          </Button>
        </div>
      </div>
      <div className="max-w-4xl mx-auto bg-white my-8 printable p-12 border border-zinc-950">
        <div className="flex items-start justify-between border-b-2 border-zinc-950 pb-6">
          <div className="flex items-start gap-4">
            <img src={LOGO_URL} alt="Apex" className="h-16 w-16 object-contain" />
            <div>
              <div className="text-2xl font-bold tracking-tight">{COMPANY.name}</div>
              <div className="text-xs text-zinc-600 mt-1 leading-relaxed">
                {COMPANY.address}<br />
                {COMPANY.phone} · {COMPANY.email}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="mono-label">RATE CONFIRMATION</div>
            <div className="font-mono text-2xl font-bold mt-1">{rc.rc_number}</div>
            <div className="mono-label mt-2">Job {rc.job_number}</div>
            {rc.po_number && <div className="mono-label">PO {rc.po_number}</div>}
            {rc.reference && <div className="mono-label">REF {rc.reference}</div>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 mt-8">
          <div>
            <div className="mono-label">CARRIER</div>
            <div className="mt-1 text-sm">
              <div className="font-semibold">{rc.carrier_name}</div>
              <div>MC # <span className="font-mono">{rc.mc_number}</span>{rc.dot_number ? <> &middot; DOT # <span className="font-mono">{rc.dot_number}</span></> : null}</div>
              {rc.carrier_contact_name && <div className="text-xs text-zinc-600 mt-1">{rc.carrier_contact_name}{rc.carrier_contact_phone && ` · ${rc.carrier_contact_phone}`}</div>}
              {rc.carrier_contact_email && <div className="text-xs text-zinc-600">{rc.carrier_contact_email}</div>}
            </div>
          </div>
          <div>
            <div className="mono-label">BILL CARRIER INVOICE TO</div>
            <div className="mt-1 text-sm">
              <div className="font-semibold">{rc.bill_to?.company || COMPANY.name}</div>
              <div>{rc.bill_to?.address || COMPANY.address}</div>
              <div className="text-xs text-zinc-600 mt-1">
                {(rc.bill_to?.phone || COMPANY.phone)} &middot; {(rc.bill_to?.email || COMPANY.email)}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 mt-8 pt-6 border-t border-zinc-200">
          <Addr label="PICKUP" a={rc.pickup} date={rc.pickup_date} />
          <Addr label="DELIVERY" a={rc.delivery} date={rc.delivery_date} extraLabel="Appointment" extraValue={rc.appointment_date} />
        </div>

        <FreightSummary doc={rc} />

        {(rc.driver_name || rc.truck_number || rc.trailer_number) && (
          <div className="mt-6 border border-zinc-200 p-4 bg-zinc-50">
            <div className="mono-label">DRIVER & EQUIPMENT</div>
            <div className="grid grid-cols-4 gap-4 mt-2 text-sm">
              <div><span className="mono-label block">Driver</span>{rc.driver_name || "—"}</div>
              <div><span className="mono-label block">Driver phone</span>{rc.driver_phone || "—"}</div>
              <div><span className="mono-label block">Truck</span><span className="font-mono">{rc.truck_number || "—"}</span></div>
              <div><span className="mono-label block">Trailer</span><span className="font-mono">{rc.trailer_number || "—"}</span></div>
            </div>
          </div>
        )}

        <div className="mt-8 border border-zinc-950">
          <div className="bg-zinc-950 text-white flex items-center justify-between px-4 py-3">
            <div className="mono-label !text-zinc-100">AGREED RATE</div>
            <div className="font-mono text-2xl font-bold">{sym}{Number(rc.agreed_rate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {rc.currency}</div>
          </div>
          <div className="p-4 grid grid-cols-2 gap-4 text-sm">
            <div><span className="mono-label block">Payment terms</span>{rc.payment_terms || "Net 30 days upon receipt of clean POD & signed BOL"}</div>
            <div><span className="mono-label block">RC issued</span><span className="font-mono">{new Date(rc.created_at).toLocaleString()}</span></div>
          </div>
        </div>

        {Array.isArray(rc.accessorials) && rc.accessorials.length > 0 && (() => {
          const accTotal = rc.accessorials.reduce((s, a) => s + (Number(a.amount) || 0), 0);
          const grand = (Number(rc.agreed_rate) || 0) + accTotal;
          return (
            <div className="mt-6 border border-zinc-950" data-testid="rc-accessorials">
              <div className="bg-zinc-950 text-white px-4 py-2 mono-label !text-zinc-100">
                ACCESSORIAL CHARGES &middot; {rc.accessorials.length}
              </div>
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="text-left py-2 px-3 mono-label">Type / description</th>
                    <th className="text-right py-2 px-3 mono-label">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {rc.accessorials.map((a, i) => {
                    const label = (a.type === "Other" ? (a.description || "Other") :
                                   a.description ? `${a.type} — ${a.description}` : a.type);
                    return (
                      <tr key={i} className={i % 2 ? "bg-zinc-50" : ""}>
                        <td className="py-2 px-3">{label}</td>
                        <td className="py-2 px-3 text-right font-mono">{sym}{Number(a.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    );
                  })}
                  <tr className="border-t-2 border-zinc-950">
                    <td className="py-2 px-3 mono-label">Line-haul rate</td>
                    <td className="py-2 px-3 text-right font-mono">{sym}{Number(rc.agreed_rate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 mono-label">Accessorials total</td>
                    <td className="py-2 px-3 text-right font-mono">{sym}{accTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                  <tr className="bg-zinc-950 text-white">
                    <td className="py-2 px-3 font-bold">TOTAL PAYABLE TO CARRIER</td>
                    <td className="py-2 px-3 text-right font-mono font-bold">{sym}{grand.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {rc.currency}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })()}

        {rc.notes && (
          <div className="mt-6 border border-zinc-200 p-4 bg-zinc-50">
            <div className="mono-label">NOTES</div>
            <p className="mt-2 text-sm whitespace-pre-wrap">{rc.notes}</p>
          </div>
        )}

        <div className="mt-8 grid grid-cols-2 gap-8">
          <div>
            <div className="mono-label mb-8">CARRIER SIGNATURE</div>
            <div className="border-b border-zinc-950 h-8" />
            <div className="text-xs mt-1 text-zinc-500">Date · Print name · Title</div>
          </div>
          <div>
            <div className="mono-label mb-8">APEX AGENT</div>
            <div className="border-b border-zinc-950 h-8" />
            <div className="text-xs mt-1 text-zinc-500">Date · Print name</div>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-zinc-200 text-xs text-zinc-500 leading-relaxed">
          <div className="mono-label">TERMS &amp; CONDITIONS</div>
          <p className="mt-2">
            By signing this rate confirmation the carrier accepts the agreed rate and payment terms above and agrees to
            Apex Supply Chain Solutions' standard broker–carrier agreement. Any accessorials must be pre-approved in
            writing. Detention billed only with confirmed check-in/check-out times. Clean, signed POD required for
            payment.
          </p>
        </div>
      </div>

      <RateConfDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        rc={rc}
        onUpdated={(next) => setRc(next)}
      />

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="sharp max-w-lg border-zinc-950">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold tracking-tight">Send rate confirmation</DialogTitle>
            <p className="text-sm text-zinc-600">The email will be sent from Apex Dispatch to the carrier.</p>
          </DialogHeader>
          <form onSubmit={send} className="space-y-3">
            <div>
              <label className="mono-label">To (carrier email) *</label>
              <Input required type="email" value={sendForm.to_email}
                onChange={(e) => setSendForm({ ...sendForm, to_email: e.target.value })}
                className="sharp mt-1 apex-input" data-testid="rc-send-to" />
            </div>
            <div>
              <label className="mono-label">CC (comma-separated)</label>
              <Input value={sendForm.cc}
                onChange={(e) => setSendForm({ ...sendForm, cc: e.target.value })}
                className="sharp mt-1 apex-input" data-testid="rc-send-cc" />
            </div>
            <div>
              <label className="mono-label">Subject (optional)</label>
              <Input value={sendForm.subject}
                onChange={(e) => setSendForm({ ...sendForm, subject: e.target.value })}
                placeholder={`Load Confirmation ${rc.rc_number} · Load #${rc.job_number}`}
                className="sharp mt-1 apex-input" data-testid="rc-send-subject" />
            </div>
            <div>
              <label className="mono-label">Extra note (optional)</label>
              <Textarea rows={4} value={sendForm.message}
                onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })}
                placeholder="Add any note that will appear above the standard details..."
                className="sharp mt-1 apex-input" data-testid="rc-send-message" />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setSendOpen(false)} className="sharp border-zinc-300">Cancel</Button>
              <Button type="submit" disabled={sending} className="sharp bg-[#0056B3] hover:bg-[#004494]" data-testid="rc-send-submit">
                <Mail className="h-4 w-4 mr-2" /> {sending ? "Sending..." : "Send email"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
